import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { CardNew } from '@/components-new/CardNew/CardNew';
import { CardNewContent } from '@/components-new/CardNew/styles';
import { copyToClipboard } from '@/helpers/helpers';
import { DATE_TIME_FORMAT, formatDate } from '@/helpers/dateHelpers/dateHelpers';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { InvoiceLinkDetailsType } from '@/types/invoiceVaultTypes';
import { QrBox } from '../../styles';
import { Mono } from '@/styles/common';
import { HighlightPanel } from '@/components-new/HighlightPanel/styles';
import { DialogButtons } from '@/components-new/Dialog/styles';
import { DetailsWidget } from '@/components/DetailsWidget/DetailsWidget';

const copy = (value: string, what: string) =>
  copyToClipboard(value, `${what} copied successfully.`).then(() => {
    amplitudeService.logEvent(AMPLITUDE_EVENTS.InvoiceLinkCopied, { what });
  });

const downloadSvg = (svg: string, name: string) => {
  const blob = new Blob([svg], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.href = url;
  a.download = `${name}.svg`;
  a.click();
  URL.revokeObjectURL(url);
};

export const ReadyToPasteCard = ({
  link,
  railLabel,
  onNew,
}: {
  link: InvoiceLinkDetailsType | null;
  railLabel: string;
  onNew: () => void;
}) => {
  if (!link) {
    return (
      <CardNew>
        <CardNewContent>
          <Typography variant='subTitle'>Ready to paste</Typography>
          <Typography variant='bodySmall'>
            Your link, QR code and a one-line snippet for the invoice footer appear here after step 3.
          </Typography>
        </CardNewContent>
      </CardNew>
    );
  }

  const kit = link.paste_kit;

  return (
    <DetailsWidget header='Ready to paste'>
      <Typography variant='subTitle'>Ready to paste</Typography>
      <Typography variant='caption'>
        {link.invoice_number} · {railLabel || link.rail_label} · {link.mode_label} · expires{' '}
        {formatDate(kit.expires_at, DATE_TIME_FORMAT)}
      </Typography>

      <Box display='flex' gap='16px' alignItems='flex-start' marginTop='20px'>
        {kit.qr_code_svg ? (
          <QrBox dangerouslySetInnerHTML={{ __html: kit.qr_code_svg }} />
        ) : (
          <QrBox>
            <Typography variant='caption'>QR rendering is not enabled on this server.</Typography>
          </QrBox>
        )}
        <Box display='flex' flexDirection='column' gap='4px' flex='1'>
          <Typography variant='caption'>Secure Verification URL</Typography>
          <HighlightPanel>
            <Mono>{kit.secure_verification_url}</Mono>
          </HighlightPanel>

          <Box display='flex' gap='20px' flexWrap='wrap' marginTop='16px'>
            <ButtonNew size='medium' onClick={() => copy(kit.secure_verification_url, 'Link')}>
              Copy link
            </ButtonNew>
            {kit.qr_code_svg && (
              <ButtonNew
                size='medium'
                variant='secondary'
                onClick={() => downloadSvg(kit.qr_code_svg, `wirevault-${link.token}`)}
              >
                Download QR
              </ButtonNew>
            )}
          </Box>
        </Box>
      </Box>

      <Typography variant='caption' marginTop='20px' marginBottom={'4px'}>
        Action Snippet HTML (paste into the invoice footer)
      </Typography>
      <HighlightPanel>
        <Mono>{kit.action_snippet_html}</Mono>
      </HighlightPanel>

      <DialogButtons justifyContent='flex-start'>
        <ButtonNew size='medium' variant='secondary' onClick={() => copy(kit.action_snippet_html, 'Snippet')}>
          Copy snippet
        </ButtonNew>
        <ButtonNew size='medium' variant='secondary' onClick={() => copy(`Token ${kit.token}`, 'Token line')}>
          Copy token line
        </ButtonNew>
      </DialogButtons>

      <Box display='flex' marginTop='26px' flexDirection='column' alignItems='flex-start' gap='20px'>
        <Box>
          <Typography variant='bodySmall'>
            Token {kit.token}. {link.rail_label} · {link.mode_label} · Expires {link.expiry_label}. The payer types the
            invoice number <b>{link.invoice_number}</b>.
          </Typography>
          <Typography variant='caption'>Appears in the Invoice links table and the audit log.</Typography>
        </Box>

        <ButtonNew size='medium' variant='secondary' onClick={onNew}>
          Make another link
        </ButtonNew>
      </Box>
    </DetailsWidget>
  );
};
